import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import Field from '../../../../Field/Field';
import { CustomCollapse } from '../../../../CustomCollapse/CustomCollapse';
import { PrevNextButtons } from '../../../../PrevNextButtons';
import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { combineUnevenArrays } from '../helpers';
import { IOtherNationality, IOtherResidence } from 'types';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';

export default function NationalityResidence() {
  const navigate = useNavigate();
  const { application } = useSelector((state: ApplicationState) => state.apply);
  const { nextRoute, previousRoute } = useTaskNavigation();

  const { initial, current } = getApplicationData(application);
  const {
    nationalityForApplicationPurposes,
    countryOfBirth,
    applyingFrom,
    primaryResidence,
    otherNationalities,
    otherResidences,
  } = current;

  const {
    nationalityForApplicationPurposes: initialNationalityForApplicationPurposes,
    countryOfBirth: initialCountryOfBirth,
    applyingFrom: initialApplyingFrom,
    primaryResidence: initialPrimaryResidence,
    otherNationalities: initialOtherNationalities,
    otherResidences: initialOtherResidences,
  } = initial;

  const showEntryIntoUkDate = primaryResidence?.entryIntoUKDate || initialPrimaryResidence?.entryIntoUKDate;
  const showDescription = primaryResidence?.description || initialPrimaryResidence?.description;
  const showAreaOfResidence = primaryResidence?.areaOfResidence || initialPrimaryResidence?.areaOfResidence;

  const combinedOtherNationalities = combineUnevenArrays<IOtherNationality>(
    otherNationalities,
    initialOtherNationalities,
  );
  const showOtherNationalities = combinedOtherNationalities?.length > 0;

  const combinedOtherResidences = combineUnevenArrays<IOtherResidence>(otherResidences, initialOtherResidences);
  const showOtherResidences = combinedOtherResidences?.length > 0;

  // this helper is necessary because edited entries with values removed (set to undefined),
  // should not be considered as 'Former', when they are really not set attributes.
  const isCurrentOrFormer = (isCurrent: boolean | undefined): string | undefined => {
    if (isCurrent === undefined) {
      return undefined;
    }
    return isCurrent ? 'Current' : 'Former';
  };

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.NATIONALITY} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          Data
        </CustomHeading>

        <Box>
          <Field
            leftLabel="Nationality"
            value={nationalityForApplicationPurposes}
            originalValue={initialNationalityForApplicationPurposes}
          />
          <Field leftLabel="Country of birth" value={countryOfBirth} originalValue={initialCountryOfBirth} />
          <Field leftLabel="Country of application" value={applyingFrom} originalValue={initialApplyingFrom} />
          <Field
            leftLabel="Country of residence"
            value={primaryResidence?.countryOfResidence}
            originalValue={initialPrimaryResidence?.countryOfResidence}
          />
          {showEntryIntoUkDate && (
            <Field
              leftLabel="UK entry date"
              value={formatYearMonthDate(primaryResidence?.entryIntoUKDate)}
              originalValue={formatYearMonthDate(initialPrimaryResidence?.entryIntoUKDate)}
            />
          )}
          {showDescription && (
            <Field
              leftLabel="UK residence category"
              value={primaryResidence?.description}
              originalValue={initialPrimaryResidence?.description}
            />
          )}
          {showAreaOfResidence && (
            <Field
              leftLabel="UK area"
              value={primaryResidence?.areaOfResidence}
              originalValue={initialPrimaryResidence?.areaOfResidence}
            />
          )}

          {/* Other nationalties now has a dropdown surrounding all other nationalites, 
          due to added visual complexity with the possility of edited and original entries showing. */}
          {showOtherNationalities && (
            <CustomCollapse key={`Other nationalities`} defaultIsOpen>
              <CustomCollapse.Trigger>{`Other nationalities`}</CustomCollapse.Trigger>

              <CustomCollapse.Content>
                {combinedOtherNationalities.map((nationalityObj, index) => (
                  <CustomCollapse
                    key={`${nationalityObj.current?.nationality ?? nationalityObj.original?.nationality}-${index}`}
                    defaultIsOpen
                  >
                    <CustomCollapse.Trigger>
                      {`Other ${
                        nationalityObj.current?.nationality ?? nationalityObj.original?.nationality ?? 'Unknown'
                      } nationality`}
                    </CustomCollapse.Trigger>

                    <CustomCollapse.Content>
                      <Field
                        leftLabel="Nationality"
                        value={nationalityObj.current?.nationality}
                        originalValue={nationalityObj.original?.nationality}
                      />
                      <Field
                        leftLabel="Current or former"
                        value={isCurrentOrFormer(nationalityObj.current?.isCurrent)}
                        originalValue={isCurrentOrFormer(nationalityObj.original?.isCurrent)}
                      />
                    </CustomCollapse.Content>
                  </CustomCollapse>
                ))}
              </CustomCollapse.Content>
            </CustomCollapse>
          )}

          {/* Other reasidences now has a dropdown surrounding all other nationalites, 
          due to added visual complexity with the possility of edited and original entries showing. */}
          {showOtherResidences && (
            <CustomCollapse key={`Other residences`} defaultIsOpen>
              <CustomCollapse.Trigger>{`Other residences`}</CustomCollapse.Trigger>

              <CustomCollapse.Content>
                {combinedOtherResidences.map((residenceObj, index) => (
                  <CustomCollapse
                    key={`${
                      residenceObj.current?.countryOfResidence ?? residenceObj.original?.countryOfResidence
                    }-${index}`}
                    defaultIsOpen
                  >
                    <CustomCollapse.Trigger>{`Residence of ${
                      residenceObj.current?.countryOfResidence ?? residenceObj.original?.countryOfResidence ?? 'Unknown'
                    }`}</CustomCollapse.Trigger>

                    <CustomCollapse.Content>
                      <Field
                        leftLabel="Country"
                        value={residenceObj.current?.countryOfResidence}
                        originalValue={residenceObj.original?.countryOfResidence}
                      />
                      <Field
                        leftLabel="Year"
                        value={residenceObj.current?.yearOfResidence}
                        originalValue={residenceObj.original?.yearOfResidence}
                      />
                    </CustomCollapse.Content>
                  </CustomCollapse>
                ))}
              </CustomCollapse.Content>
            </CustomCollapse>
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
